import { RightOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Card, List, Switch, Tag, Typography } from 'antd';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router';
import { skladsApi, storesApi } from '../api/endpoints';
import { queryKeys } from '../api/queryKeys';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/PageStates';
import { useStoreContext } from '../context/StoreContext';
import { balanceColor } from '../lib/balanceColors';
import { formatKopeks } from '../lib/money';

interface DirectoryRow {
  id: string;
  name: string;
  isArchived: boolean;
  /** Store balance or sklad debt. */
  money: number;
}

/**
 * /stores and /sklads lists (STEP-06). Rows show the big colored balance;
 * archived entities appear behind a toggle. With a store selected in the
 * global context, /stores jumps straight into that store's ledger.
 */
export function DirectoryListPage({ kind }: { kind: 'store' | 'sklad' }): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { storeId: ctxStoreId, storeIds: ctxStoreIds } = useStoreContext();
  const [showArchived, setShowArchived] = useState(false);

  const storesQuery = useQuery({
    queryKey: queryKeys.stores(showArchived),
    queryFn: () => storesApi.list(showArchived),
    enabled: kind === 'store',
  });
  const skladsQuery = useQuery({
    queryKey: queryKeys.sklads(showArchived),
    queryFn: () => skladsApi.list(showArchived),
    enabled: kind === 'sklad',
  });
  const query = kind === 'store' ? storesQuery : skladsQuery;
  const rows: DirectoryRow[] =
    kind === 'store'
      ? (storesQuery.data ?? [])
          // A user-scope SET keeps the list, narrowed to that user's stores.
          .filter((s) => !ctxStoreIds || ctxStoreIds.includes(s.id))
          .map((s) => ({
            id: s.id,
            name: s.name,
            isArchived: s.isArchived,
            money: s.balance,
          }))
      : (skladsQuery.data ?? []).map((s) => ({
          id: s.id,
          name: s.name,
          isArchived: s.isArchived,
          money: s.debt,
        }));

  // Global store context narrows /stores to the selected store's ledger.
  if (kind === 'store' && ctxStoreId) return <Navigate to={`/stores/${ctxStoreId}`} replace />;

  return (
    <Card
      size="small"
      title={kind === 'store' ? t('nav.stores') : t('nav.sklads')}
      extra={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {t('common.showArchived')}
          </Typography.Text>
          <Switch checked={showArchived} onChange={setShowArchived} />
        </span>
      }
    >
      {query.isLoading ? (
        <LoadingBlock />
      ) : query.isError ? (
        <ErrorBlock onRetry={() => void query.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyBlock
          description={kind === 'store' ? t('stores.empty') : t('sklads.empty')}
          hint={kind === 'store' ? t('stores.emptyHint') : t('sklads.emptyHint')}
        />
      ) : (
        <List
          dataSource={rows}
          renderItem={(row) => (
            <List.Item
              onClick={() => navigate(`/${kind === 'store' ? 'stores' : 'sklads'}/${row.id}`)}
              style={{ cursor: 'pointer', paddingInline: 4 }}
              extra={<RightOutlined style={{ color: '#bbb' }} />}
            >
              <List.Item.Meta
                title={
                  <>
                    {row.name}
                    {row.isArchived ? (
                      <Tag style={{ marginInlineStart: 8 }}>{t('common.archivedTag')}</Tag>
                    ) : null}
                  </>
                }
              />
              <Typography.Text
                strong
                style={{
                  marginInlineEnd: 12,
                  fontSize: 17,
                  color: balanceColor(kind, row.money),
                }}
              >
                {formatKopeks(row.money)}
              </Typography.Text>
            </List.Item>
          )}
        />
      )}
    </Card>
  );
}
